import { Box, Link as MuiLink, Stack, Tooltip, Typography } from '@mui/material'
import { C1Link } from 'components/core/links/C1Link'
import { ActionTaskIcon, EmergencyAccessTaskIcon, ExtensionGrantTaskIcon, GrantTaskIcon, ReviewTaskIcon, RevokeTaskIcon } from 'components/core/task/task-type'
import { TaskDrawer } from 'components/pages/task/details/drawer/TaskDrawer'
import { useDrawer } from 'hooks/useDrawer'
import { TaskValue } from 'lib/actions/task'
import { DisplayModeContext } from 'lib/context/app'
import { routes } from 'lib/routes'
import { FC, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type TaskViewLinkProps = {
    task: TaskValue;
    readOnly?: boolean;
    hasPendingActions?: boolean;
};

const getIcon = (taskType?: string): React.ReactElement => {
    switch (taskType) {
        case 'grant':
            return <GrantTaskIcon />
        case 'revoke':
            return <RevokeTaskIcon />
        case 'action':
            return <ActionTaskIcon />
        default:
            return <ReviewTaskIcon />
    }
}

export const TaskViewLinkV2: FC<TaskViewLinkProps> = ({ task, readOnly = false, hasPendingActions = false }) => {
    const displayContext = useContext(DisplayModeContext)
    const isInModal = displayContext === 'dialog' || displayContext === 'drawer'

    const intl = useIntl()

    const [showDrawer, closeDrawer] = useDrawer()
    const href = task.numericId ? routes.task(String(task.numericId)) : undefined

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        const isLeftClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey

        // Only try opening in a modal when we aren't already in a modal, and when the user isn't modifier-clicking
        if (isLeftClick && !isInModal) {
            e.preventDefault()
            showDrawer(
                <TaskDrawer
                    close={closeDrawer}
                    taskId={task.id}
                    hasPendingActions={hasPendingActions}
                />,
                { variant: 'md' },
            )
        }
    }

    const isExtensionGrantTask = task.type?.taskType.oneofKind === 'grant' && !!task.type?.taskType.grant.source?.isExtension

    const innerContent = <Stack direction="row" spacing={1} alignItems="center">
        {getIcon(task.type?.taskType.oneofKind)}
        {task.type?.taskType.oneofKind === 'grant' && task.emergencyAccess && <EmergencyAccessTaskIcon />}
        {isExtensionGrantTask && <ExtensionGrantTaskIcon />}
        <Typography component="span">
            {task.numericId || <FormattedMessage id="components_view_link" defaultMessage="View" />}
        </Typography>
    </Stack>

    return !readOnly
        ? <Tooltip
            placement="right"
            title={isInModal
                ? intl.formatMessage({ id: 'components_task_view_link_tooltip', defaultMessage: 'Open task #{id} in a new tab' }, { id: task.numericId })
                : intl.formatMessage({ id: 'components_task_view_link_tooltip', defaultMessage: 'View task #{id}' }, { id: task.numericId })
            }
        >
            <MuiLink
                onClick={handleLinkClick}
                component={href ? C1Link : 'span'}
                href={href}
                target={isInModal ? '_blank' : '_self'} // Always open in a new tab if already in a modal
            >
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        borderRadius: 1,
                        display: 'inline-flex',
                        py: 1,
                        px: 2,
                        backgroundColor: 'background.default',
                        '&:hover': { borderColor: 'grey.500' },
                    }}
                >
                    {innerContent}
                </Box>
            </MuiLink>
        </Tooltip>
        : innerContent
}
